import cookies from 'react-cookies';
import { batch } from 'react-redux';
import App from 'next/app';

import { initAuth, setCSRF } from '@r1-frontend/entities/stores/auth/actionCreators';
import { getAuthDataFromCookies } from '@r1-frontend/entities/stores/auth/helpers/getAuthDataFromCookies';
import { setMenuData } from '@r1-frontend/entities/stores/city/actionCreators/menu';
import { setAbnVariants, setCurrentCity, setProviderId } from '@r1-frontend/entities/stores/city/actions';

import { PROVIDER_ID_COOKIE } from '@r1-frontend/shared/constants';
import Cookies from '@r1-frontend/shared/helpers/Cookies';

import InitialCity from '~/src/app/city/InitialCity';
import { withoutMiddlewaresList } from '~/src/app/middlewares/middleware.config';
import { isrPaths } from '~/src/constants/isrPaths';
import getRequester from '~/src/helpers/getRequester';
import selectProviderId from '~/src/store/selectors/selectProviderId';

import { wrapper } from './store/createStore';

/**
 * Attention:
 * getInitialAppProps has be called on the Client-Side and Server Side both
 *
 */
const baseInitialProps = wrapper.getInitialAppProps(store => async appContext => {
    const isISR = isrPaths.some(p => appContext.ctx.pathname.endsWith(p));
    if (isISR) {
        return {
            pageProps: {},
        };
    }

    const dispatch = store.dispatch;

    dispatch(initAuth(getAuthDataFromCookies(appContext.ctx)));

    const withoutMiddlewares = withoutMiddlewaresList.some(
        path => appContext.ctx.req?.url && appContext.ctx.req.url.startsWith(path),
    );
    /**
     * We already have a rehydrated store on the client side,
     * so we will only receive data on the server
     */
    if (appContext.ctx.req && appContext.ctx.res && !withoutMiddlewares) {
        const cookiesInstance = new Cookies(appContext.ctx);
        const doRequest = getRequester(appContext.ctx.req);
        const state = store.getState();

        /**
         * This is rewritten in
         * https://gitlab.com/r1team/d2c/frontend/b2c-domru/-/blob/master/frontend/apps/b2c-domru/src/middlewares/withFinalRewrite.ts?ref_type=heads#L18,
         * so we have to read the city from query params.
         */
        const cityFromHost = appContext.ctx.query['city'];
        const providerIdFromCookie =  cookiesInstance.get(PROVIDER_ID_COOKIE);
        const isLk = !!appContext.ctx.req.url?.startsWith('/lk');

        // if providerId is already in store, we want to skip the next step.
        const providerId = selectProviderId(state);
        if (!providerId && (cityFromHost || (isLk && providerIdFromCookie))) {
            const initiator = new InitialCity(appContext.ctx);
            const {
                providerId: nextProviderId,
                currentCity,
                abnVariants,
                menus,
            } = await initiator.getAll();
            batch(() => {
                dispatch(setProviderId(nextProviderId));
                dispatch(setCurrentCity(currentCity));
                dispatch(setAbnVariants(abnVariants));

                // todo: Store: Меню нужно перенести к текущему городу
                dispatch(setMenuData(menus));
            });
        }

        // TODO: Пробросить ip-шник в headers (при этом на песке не проверить)
        // TODO: Вызов тут по внутрикластерному должен идти
        const resp = await doRequest.b2cDomru<{ token: string }>({
            uri: '/api/csrf-token',
            method: 'GET',
        });
        if (resp.isSuccess) {
            dispatch(setCSRF(resp.payload.token));
        }
    }

    // todo: Remove it after removing /v1/info/all from Server Side
    if (appContext.ctx.req) {
        const { req, res } = appContext.ctx;
        // @ts-ignore
        cookies.plugToRequest(req, res);
    }

    return {
        pageProps: {
            ...(await App.getInitialProps(appContext)).pageProps,
        },
    };
});

export default baseInitialProps;
